import {
  baseComponentFragment,
  documentWithHyperlinkEntriesFragment,
  documentWithInlineEntriesTextFragment,
  internalPageFragment,
  modalFragment,
  videoItemFragment,
  documentWithCommonEntriesFragment,
  hyperlinkedMediaFragment,
} from '@/libs/contentful/fragments';

export const availableFragments = {
  SysId: `
    fragment SysId on Entry {
      sys { id }
    }
  `,
  MediaFields: `
    fragment MediaFields on Asset {
      title
      description
      contentType
      fileName
      size
      url
      width
      height
    }
  `,
  CtaFields: `
    fragment CtaFields on Cta {
      sys { id }
      text
      url
      internalPage {
        ...InternalPageRef
      }
      variant
    }
  `,
  ContentfulMetadata: `
    fragment ContentfulMetadata on Entry {
      contentfulMetadata {
        tags { id }
      }
    }
  `,
  InternalPageRef: `
    fragment InternalPageRef on Page {
      sys { id }
      slug
    }
  `,
  LinebreakFields: `
    fragment LinebreakFields on Linebreak {
      ...SysId
      breakBehavior
      testId
    }
  `,
  LinkFields: `
    fragment LinkFields on Link {
      sys { id }
      label
      url
      target
      id
      class
      internalPage {
        ...InternalPageRef
      }
    }
  `,
  LinkedIconFields: `
    fragment LinkedIconFields on LinkedIcon {
      sys { id }
      label
      showLabel
      internalPage {
        ...InternalPageRef
      }
      url
      iconName
      icon {
        ...MediaFields
      }
      labelPlacement
    }
  `,
  SocialMediaLinkFields: `
    fragment SocialMediaLinkFields on SocialMediaLink {
      sys { id }
      iconName
      icon {
        ...MediaFields
      }
      url
      label
      showLabel
    }
  `,
  DisclosureItemFields: `
    fragment DisclosureItemFields on DisclosureItem {
      ...SysId
      copy {
        json
        links {
          entries {
            inline {
              __typename
              ... on Link {
                ...LinkFields
              }
            }
          }
        }
      }
    }
  `,
  DisclosureSymbolFields: `
    fragment DisclosureSymbolFields on DisclosureSymbol {
      ...SysId
      symbol
    }
  `,
  DisclosureFields: `
    fragment DisclosureFields on Disclosure {
      ...SysId
      __typename
      symbol
      copy {
        json
      }
    }
  `,
  DisclosureFull: `
    fragment DisclosureFull on Disclosure {
      ...SysId
      __typename
      symbol
      copy {
        json
      }
      disclosureItem {
        ...DisclosureItemFields
      }
      disclosureSymbol {
        ...DisclosureSymbolFields
      }
    }
  `,
  ModalFields: `
    fragment ModalFields on Modal {
      ...SysId
      copy {
        json
      }
      headline
      disclaimerText
    }
  `,
  ShortcodeFields: `
    fragment ShortcodeFields on Shortcode {
      ...SysId
      shortCodeValue {
        json
      }
    }
  `,
  // inline fields common to all rich text inline fields
  CommonInlineFields: `
    fragment CommonInlineFields on Entry {
      ...SysId
      __typename
      ... on Disclosure {
        ...DisclosureFields
      }
      ... on Linebreak {
        ...LinebreakFields
      }
      ... on Modal {
        ...ModalFields
      }
      ... on Shortcode {
        ...ShortcodeFields
      }
    }
  `,
  HeroFields: `
    fragment HeroFields on HeroConsolidated {
      ${baseComponentFragment}
      variant
      headline {
        ${documentWithInlineEntriesTextFragment}
      }
      copy {
        ${documentWithHyperlinkEntriesFragment}
      }
      eyebrowText {
        ${documentWithCommonEntriesFragment}
      }
      eyebrowAlternateLogo {...MediaFields}
      eyebrowChimeLogo
      blogDate
      showEmailInput
      emailInputButtonText
      cta {
        ...CtaFields
      }
      disclaimers {
        ${documentWithHyperlinkEntriesFragment}
      }
      margins
      mobileMediaLocation
      disclaimerModal {
        ${modalFragment}
      }
      color
      scrollCta {...CtaFields}
      hyperlinkedMediaCollection(limit: 2) {
        items {${hyperlinkedMediaFragment}}
      }
      headlineSize
      copySize
      author {
        ...SysId
        name
        image {...MediaFields}
        title
        url
        internalPage {${internalPageFragment}}
      }
      desktopMedia {...MediaFields}
      desktopAspectRatio {
        value
      }
      mobileMedia {...MediaFields}
      mobileAspectRatio {
        value
      }
      imageOverlay {...MediaFields}
      notificationOverlayDesktopPosition
      allowPromoCodeEntry
      promoCodeInputButtonText
      promoCodes
      video {${videoItemFragment}}
      showBlogImage
      blogImageSize
    }
  `,
};

export function getUsedFragments(queryString: string): string[] {
  const usedFragments: string[] = [];
  Object.keys(availableFragments).forEach(fragmentName => {
    if (queryString.includes(`...${fragmentName}`)) {
      usedFragments.push(fragmentName);
    }
  });
  return usedFragments;
}

export function getFragmentDependencies(fragmentName: string): string[] {
  const fragmentContent = availableFragments[fragmentName as keyof typeof availableFragments];
  const dependencies: string[] = [];
  Object.keys(availableFragments).forEach(depName => {
    if (fragmentContent.includes(`...${depName}`)) {
      dependencies.push(depName);
    }
  });
  return dependencies;
}

export function getAllRequiredFragments(initialFragments: string[]): string[] {
  const allFragments = new Set(initialFragments);
  let hasNewFragments = true;
  while (hasNewFragments) {
    hasNewFragments = false;
    const currentFragments = Array.from(allFragments);
    for (const fragmentName of currentFragments) {
      const dependencies = getFragmentDependencies(fragmentName);
      for (const dep of dependencies) {
        if (!allFragments.has(dep)) {
          allFragments.add(dep);
          hasNewFragments = true;
        }
      }
    }
  }
  return Array.from(allFragments);
}

export function appendFragmentsToQuery(query: string): string {
  const initialUsedFragmentNames = getUsedFragments(query);
  const usedFragmentNames = getAllRequiredFragments(initialUsedFragmentNames);
  const fragmentDefinitions = usedFragmentNames
    .map(name => availableFragments[name as keyof typeof availableFragments])
    .join('\n');
  return query + fragmentDefinitions;
}
